import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Download, FileSpreadsheet, Loader2, PieChart } from 'lucide-react';
import { toast } from 'sonner';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sepa-stripe/admin/taux-encaissement`;
const eur = (v) => Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const pct = (v) => (v === null || v === undefined ? '—' : `${String(v).replace('.', ',')} %`);
const MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const libMois = (m) => `${MOIS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;

const Carte = ({ titre, valeur, sous, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testid}>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{titre}</p>
    <p className="mt-1 text-xl font-bold text-[#0a3d62]">{valeur}</p>
    {sous && <p className="text-[11px] text-slate-500">{sous}</p>}
  </div>
);

const DetailPrecis = ({ lignes, onChoisir }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200" data-testid="taux-detail-precis">
    <p className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Détail par dispositif précis</p>
    <table className="w-full min-w-[800px] text-xs">
      <thead className="text-left text-slate-500"><tr>
        <th className="p-2">Dispositif</th><th className="p-2">Total</th><th className="p-2">CB / SEPA / Virement</th>
        <th className="p-2">Réussite CB</th><th className="p-2">Rejet SEPA (nb)</th><th className="p-2">Réussite virement</th><th className="p-2">Frais</th>
      </tr></thead>
      <tbody>
        {lignes.map((t) => (
          <tr key={t.dispositif} className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-slate-50" onClick={() => onChoisir(t.dispositif)} data-testid={`taux-precis-${t.dispositif.replace(/\s+/g, '-').toLowerCase()}`}>
            <td className="p-2 font-semibold text-[#0a3d62]">{t.dispositif}<span className="ml-1 font-normal text-slate-400">({t.famille})</span></td>
            <td className="p-2 font-semibold">{eur(t.total)}</td>
            <td className="p-2">{pct(t.part_cb)} · {pct(t.part_sepa)} · {pct(t.part_virement)}</td>
            <td className="p-2">{pct(t.taux_reussite_cb)}</td>
            <td className={`p-2 ${t.taux_rejet_nb ? 'font-semibold text-rose-700' : ''}`}>{pct(t.taux_rejet_nb)} <span className="text-slate-400">({t.sepa_rejets}/{t.sepa_presentes})</span></td>
            <td className="p-2">{pct(t.taux_reussite_virement)}</td>
            <td className="p-2">{eur(t.frais)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Factures & Reçus ESS : taux d'encaissement par dispositif et par mois. */
export default function TauxEncaissement() {
  const { getAuthHeader } = useAuth();
  const [annee, setAnnee] = useState(0);
  const [disp, setDisp] = useState('Tous');
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    axios.get(API, { headers: getAuthHeader(), params: annee ? { annee } : {} })
      .then(({ data: d }) => setData(d)).catch(() => setData({ lignes: [], totaux: [], dispositifs: [], mois: [] }));
  }, [annee, getAuthHeader]);
  const precis = disp.startsWith('precis::') ? disp.slice(8) : null;
  const lignes = useMemo(() => (precis ? data?.lignes_detail || [] : data?.lignes || [])
    .filter((l) => l.dispositif === (precis || disp)), [data, disp, precis]);
  const tot = precis ? (data?.totaux_detail || []).find((t) => t.dispositif === precis) : (data?.totaux || []).find((t) => t.dispositif === disp);
  const detail = (data?.totaux_detail || []).filter((t) => !precis && (disp === 'Tous' || t.famille === disp));
  const an = new Date().getFullYear();
  const [export_, setExport] = useState('');
  const telecharger = async (format) => {
    setExport(format);
    try {
      const r = await axios.get(`${API}/export`, { headers: getAuthHeader(), params: { format, ...(annee ? { annee } : {}) }, responseType: 'blob' });
      const nom = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || `Taux_encaissement.${format}`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = nom; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export téléchargé — une copie est classée dans la GED (Comptabilité › Rapports mensuels)');
    } catch { toast.error("Export impossible"); }
    setExport('');
  };

  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="taux-encaissement">
      <div className="flex flex-wrap items-center gap-3">
        <PieChart className="h-5 w-5 text-[#0a3d62]" />
        <h3 className="flex-1 text-base font-bold text-[#0a3d62]">Taux d&apos;encaissement par dispositif</h3>
        <select value={annee} onChange={(e) => setAnnee(Number(e.target.value))} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" data-testid="taux-periode">
          <option value={0}>12 derniers mois</option>
          {[an, an - 1, an - 2, an - 3].map((y) => <option key={y} value={y}>Année {y}</option>)}
        </select>
        <button type="button" onClick={() => telecharger('xlsx')} disabled={!!export_} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600 px-3 py-1.5 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:opacity-50" data-testid="taux-export-excel">
          {export_ === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Excel
        </button>
        <button type="button" onClick={() => telecharger('pdf')} disabled={!!export_} className="inline-flex items-center gap-1.5 rounded-lg border border-[#0a3d62] px-3 py-1.5 text-sm font-semibold text-[#0a3d62] transition-colors hover:bg-slate-50 disabled:opacity-50" data-testid="taux-export-pdf">
          {export_ === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
        </button>
        <select value={disp} onChange={(e) => setDisp(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" data-testid="taux-dispositif">
          <option value="Tous">Tous les dispositifs</option>
          {(data?.dispositifs || []).map((f) => (
            <optgroup key={f} label={f}>
              <option value={f}>{f} — toute la famille</option>
              {(data?.details?.[f] || []).map((p) => <option key={p} value={`precis::${p}`}>{p}</option>)}
            </optgroup>
          ))}
        </select>
      </div>
      {!data ? <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Calcul des taux…</div> : (
        <>
          {tot && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Carte titre="Total encaissé" valeur={eur(tot.total)} sous={`CB ${pct(tot.part_cb)} · SEPA ${pct(tot.part_sepa)} · Virement ${pct(tot.part_virement)}`} testid="taux-total" />
              <Carte titre="Réussite CB" valeur={pct(tot.taux_reussite_cb)} sous={`${tot.nb_cb} réussi(s) · ${tot.cb_echecs} échec(s)`} testid="taux-reussite-cb" />
              <Carte titre="Rejets SEPA" valeur={pct(tot.taux_rejet_nb)} sous={`${tot.sepa_rejets}/${tot.sepa_presentes} prélèvements · ${pct(tot.taux_rejet_montant)} du montant`} testid="taux-rejet-sepa" />
              <Carte titre="Réussite virement · Frais" valeur={pct(tot.taux_reussite_virement)} sous={`${tot.virements_encaisses}/${tot.virements_declares} virements déclarés encaissés · frais ${eur(tot.frais)}`} testid="taux-virement-frais" />
            </div>
          )}
          {detail.length > 0 && <DetailPrecis lignes={detail} onChoisir={(p) => setDisp(`precis::${p}`)} />}
          <div className="h-64" data-testid="taux-graphique">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={lignes.map((l) => ({ ...l, libelle: libMois(l.mois) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="libelle" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)} k€`} />
                <Tooltip formatter={(v) => eur(v)} />
                <Legend />
                <Bar dataKey="cb" name="CB" stackId="a" fill="#0a3d62" />
                <Bar dataKey="sepa" name="SEPA" stackId="a" fill="#DEA900" />
                <Bar dataKey="virement" name="Virement" stackId="a" fill="#059669" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-xs">
              <thead className="text-left uppercase text-slate-500">
                <tr>
                  <th className="p-2">Mois</th><th className="p-2">Total</th><th className="p-2">Part CB</th><th className="p-2">Part SEPA</th><th className="p-2">Part virement</th>
                  <th className="p-2">Réussite CB</th><th className="p-2">Rejet SEPA (nb)</th><th className="p-2">Rejet SEPA (€)</th><th className="p-2">Réussite virement</th><th className="p-2">Frais encaissés</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((l) => (
                  <tr key={l.mois} className="border-t border-slate-100" data-testid={`taux-ligne-${l.mois}`}>
                    <td className="p-2 font-semibold">{libMois(l.mois)}</td>
                    <td className="p-2 font-semibold">{eur(l.total)}</td>
                    <td className="p-2">{pct(l.part_cb)}<br /><span className="text-slate-400">{eur(l.cb)}</span></td>
                    <td className="p-2">{pct(l.part_sepa)}<br /><span className="text-slate-400">{eur(l.sepa)}</span></td>
                    <td className="p-2">{pct(l.part_virement)}<br /><span className="text-slate-400">{eur(l.virement)}</span></td>
                    <td className="p-2">{pct(l.taux_reussite_cb)}<br /><span className="text-slate-400">{l.nb_cb} / {l.nb_cb + l.cb_echecs}</span></td>
                    <td className={`p-2 ${l.taux_rejet_nb ? 'font-semibold text-rose-700' : ''}`}>{pct(l.taux_rejet_nb)}<br /><span className="text-slate-400">{l.sepa_rejets} / {l.sepa_presentes}</span></td>
                    <td className={`p-2 ${l.taux_rejet_montant ? 'font-semibold text-rose-700' : ''}`}>{pct(l.taux_rejet_montant)}<br /><span className="text-slate-400">{eur(l.sepa_montant_rejete)}</span></td>
                    <td className="p-2">{pct(l.taux_reussite_virement)}<br /><span className="text-slate-400">{l.virements_encaisses} / {l.virements_declares}</span></td>
                    <td className="p-2">{eur(l.frais)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500">Part = poids du mode dans le total encaissé du mois. Réussite CB = paiements réussis / (réussis + échecs de prélèvement carte). Rejet SEPA = prélèvements rejetés / présentés (en nombre et en montant). Réussite virement = virements déclarés dans le mois puis encaissés / déclarés.</p>
        </>
      )}
    </div>
  );
}
